import React from 'react';
import qs from 'qs';
import { useTranslation } from 'react-i18next';

import { CampaignStatus } from '@/services/ws-digital/src/main/resources/rest-api';
import { getAgencyDashboardLinks } from '@/services/ws-orders/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSearchParams, generatePath, useMatch } from '@/js/libs/router';
import { useIdentityRoles } from '@/js/libs/use-auth';
import { useRequestLegacy } from '@/js/libs/use-request-legacy';

import { ROUTER } from 'src/AppRouter.tsx';
import type { NavItemProps } from 'src/components/SceneSubHeader/index.tsx';
import SceneSubHeader from 'src/components/SceneSubHeader/index.tsx';
import { getStatuses } from './helpers.ts';
import { PositionsType } from './types.ts';

const isCampaignStatus = (value: string): value is CampaignStatus => Object.values(CampaignStatus).includes(value as CampaignStatus);

interface IStageFilter {
  positionsType: PositionsType;
  filters?: React.ReactNode;
}

// TODO This component will removed redesign of all position types pages
const StageFilter: React.FC<IStageFilter> = ({ positionsType, filters }) => {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard']);
  const { SELF_BOOKING } = useIdentityRoles();
  const [searchParams] = useSearchParams();
  const match = useMatch(ROUTER['/orders/campaigns/:type/:campaignId'].path.pattern);
  const [agencyDashboardStatuses, { pending: isLoadingAgencyDashboardStatuses, error }] = useRequestLegacy({
    apiMethod: getAgencyDashboardLinks,
  });

  const statuses = React.useMemo(
    (): string[] => getStatuses(agencyDashboardStatuses, positionsType, { SELF_BOOKING }),
    [SELF_BOOKING, agencyDashboardStatuses, positionsType],
  );

  const getTo = useCallbackRef((status: string): string => {
    if (positionsType === PositionsType.CAMPAIGNS) {
      return ROUTER['/orders/campaigns'].path({ type: isCampaignStatus(status) ? status : CampaignStatus.ALL });
    }
    return generatePath('/orders/:positionsType/:status', {
      positionsType,
      status,
    });
  });

  const navItems = React.useMemo<NavItemProps[]>(
    () =>
      statuses.map((status) => ({
        to: `${getTo(status)}${qs.stringify(searchParams, { skipNulls: true, addQueryPrefix: true })}`,
        label: t(status),
        dataTestId: `status-${status}-link`,
        getOverriddenActive: positionsType === PositionsType.CAMPAIGNS ? () => match?.params.type === status : undefined,
      })),
    [statuses, getTo, searchParams, t, positionsType, match?.params.type],
  );

  return (
    <SceneSubHeader items={navItems} error={error?.message} pending={isLoadingAgencyDashboardStatuses}>
      {filters}
    </SceneSubHeader>
  );
};

export default React.memo(StageFilter);
